import { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import axios from "axios";
import { Hourglass, ArrowRight, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * MegaMenu — desktop header navigation.
 *
 * Each department is its OWN top-level menu item (Clothing · Footwear · Bags · Jewellery · Accessories).
 * Hovering a department reveals an H&M-inspired panel:
 *   • left: dept highlights (Shop all / New arrivals / On sale)
 *   • middle: category list with thumbnails (multi-column)
 *   • right: editorial image with "Shop now" CTA
 *
 * The panel is anchored to the centre of the viewport so it never gets clipped at screen edges.
 * Hidden on mobile; the mobile drawer accordion serves the same purpose.
 */
export default function MegaMenu() {
    const [data, setData] = useState([]); // [{slug, name, status, cover_image, categories:[...]}]
    const [openSlug, setOpenSlug] = useState(null);
    const closeTimer = useRef(null);
    const openTimer = useRef(null);
    // We track whether ANY mega-menu panel is currently open. When the
    // shopper has already opened one panel, switching between department
    // labels should feel instant — but the first open requires a short
    // intent dwell so brief mouse-throughs don't trigger anything.
    const openSlugRef = useRef(null);
    useEffect(() => { openSlugRef.current = openSlug; }, [openSlug]);

    useEffect(() => {
        let alive = true;
        Promise.all([
            axios.get(`${API}/departments`),
            axios.get(`${API}/categories?storefront=true`),
        ]).then(([d, c]) => {
            if (!alive) return;
            const cats = c.data || [];
            const byDept = new Map();
            for (const cat of cats) {
                const slug = cat.department_slug || "clothing";
                if (!byDept.has(slug)) byDept.set(slug, []);
                byDept.get(slug).push(cat);
            }
            const enriched = (d.data || []).map(dept => ({
                ...dept,
                categories: byDept.get(dept.slug) || [],
            }));
            setData(enriched);
        }).catch(() => {});
        return () => { alive = false; };
    }, []);

    // Cleanup pending timers on unmount.
    useEffect(() => () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        if (openTimer.current) clearTimeout(openTimer.current);
    }, []);

    /**
     * Hover-intent open.
     * - First open: 220ms dwell required → ignores fly-over passes.
     * - Subsequent swaps (panel already open): instant → switching
     *   between dept labels feels native.
     * - Cancels any pending close, so re-entering before the close
     *   timeout fires keeps the menu visible.
     */
    const openDept = useCallback((slug) => {
        if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
        if (openTimer.current) { clearTimeout(openTimer.current); openTimer.current = null; }
        if (openSlugRef.current) {
            // Menu already open elsewhere — swap immediately.
            setOpenSlug(slug);
            return;
        }
        openTimer.current = setTimeout(() => {
            setOpenSlug(slug);
            openTimer.current = null;
        }, 220);
    }, []);

    /**
     * Hover-intent close.
     * - 220ms grace before hiding so a small mouse jitter between the
     *   label and panel doesn't snap the panel shut.
     * - Also cancels any pending OPEN timer (so if the customer briefly
     *   touched a label then bailed, nothing opens).
     */
    const scheduleClose = useCallback(() => {
        if (openTimer.current) { clearTimeout(openTimer.current); openTimer.current = null; }
        closeTimer.current = setTimeout(() => setOpenSlug(null), 220);
    }, []);

    /**
     * Explicit cancel — used when re-entering a label/panel before the
     * close timeout has fired.
     */
    const cancelClose = useCallback(() => {
        if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    }, []);

    /**
     * Close NOW — fired on any link click. The panel is hover-driven, so
     * without this a click navigates (SPA, no reload) while the panel stays
     * open over the destination — no feedback the link worked, and clicking a
     * link for the dept you're already hovering looks like nothing happened.
     * Clears pending open/close timers so nothing re-opens after navigation.
     */
    const closeMenu = useCallback(() => {
        if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
        if (openTimer.current) { clearTimeout(openTimer.current); openTimer.current = null; }
        setOpenSlug(null);
    }, []);

    if (data.length === 0) return null;

    return (
        <nav
            className="relative hidden lg:flex items-center gap-1"
            onMouseLeave={scheduleClose}
            data-testid="mega-menu-root"
        >
            {/* Backdrop — portalled to <body> so it escapes the header's stacking context.
                Opaque dim + heavy blur so the page content fully recedes when menu opens. */}
            {openSlug && typeof document !== "undefined" && createPortal(
                <div
                    className="fixed inset-x-0 bottom-0 z-[80] pointer-events-none transition-opacity duration-200"
                    style={{ top: "96px", background: "rgba(0, 0,0,0.30)", backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)" }}
                    aria-hidden="true"
                    data-testid="mega-menu-backdrop"
                />,
                document.body
            )}
            {data.map(d => {
                const isOpen = openSlug === d.slug;
                const isLive = d.status === "live";
                return (
                    <div key={d.slug} className="static" onMouseEnter={() => openDept(d.slug)}>
                        <Link
                            to={`/d/${d.slug}`}
                            data-testid={`mega-top-${d.slug}`}
                            onFocus={() => openDept(d.slug)}
                            onBlur={scheduleClose}
                            onClick={closeMenu}
                            className={`inline-flex items-center gap-1.5 px-3 h-10 text-[13px] font-semibold uppercase tracking-wider rounded-md transition-colors ${
                                isOpen
                                    ? "text-rosegold"
                                    : isLive ? "text-ink hover:text-rosegold" : "text-ink-soft hover:text-rosegold"
                            }`}
                        >
                            {d.name}
                            {!isLive && (
                                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-wine text-ivory text-[9px] font-bold normal-case tracking-wider">
                                    <Hourglass size={8} /> Soon
                                </span>
                            )}
                        </Link>
                    </div>
                );
            })}

            {openSlug && (() => {
                const dept = data.find(d => d.slug === openSlug);
                if (!dept) return null;
                return createPortal(
                    <div
                        className="fixed left-1/2 -translate-x-1/2 z-[90] px-4"
                        style={{ top: "96px", width: "min(1100px, 96vw)" }}
                        onMouseEnter={() => { cancelClose(); openDept(openSlug); }}
                        onMouseLeave={scheduleClose}
                        // Event delegation: any link click inside the panel closes
                        // it (covers every current + future link in one place). A
                        // click on non-link padding is ignored so the panel doesn't
                        // vanish under the cursor mid-browse.
                        onClick={(e) => { if (e.target.closest("a")) closeMenu(); }}
                        data-testid="mega-menu-panel"
                    >
                        <MegaPanel dept={dept} />
                    </div>,
                    document.body
                );
            })()}
        </nav>
    );
}

function MegaPanel({ dept }) {
    const isLive = dept.status === "live";
    const cats = dept.categories;
    // Split cats into 2 columns when there are many
    const half = Math.ceil(cats.length / 2);
    const col1 = cats.slice(0, half);
    const col2 = cats.slice(half);
    const editorialImage = dept.cover_image || (cats.find(c => c.image_url)?.image_url) || null;

    // Coming-soon panel — same skeleton as live (highlights left · waitlist message middle · editorial image right)
    if (!isLive) {
        return (
            <div className="bg-white border-2 border-rosegold/30 rounded-2xl shadow-[0_30px_90px_-12px_rgba(26,24,24,0.55),0_0_0_1px_rgba(255,255,255,0.6)_inset] overflow-hidden ring-1 ring-white/30">
                <div className="grid grid-cols-[200px_1fr_280px] min-h-[320px]">
                    {/* Highlights column */}
                    <div className="bg-ivory-soft border-r border-rosegold/10 p-5">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-wine text-ivory text-[9px] uppercase tracking-widest font-bold mb-3">
                            <Hourglass size={9} /> Soon
                        </span>
                        <p className="overline text-rosegold mb-3">{dept.name}</p>
                        <ul className="space-y-1.5">
                            <li>
                                <Link to={`/d/${dept.slug}`} data-testid={`mega-shop-all-${dept.slug}`} className="flex items-center gap-1.5 text-sm font-bold text-ink hover:text-rosegold py-1">
                                    <ArrowRight size={13} /> About {dept.name}
                                </Link>
                            </li>
                            <li>
                                <Link to={`/d/${dept.slug}`} className="inline-flex items-center gap-1 text-sm text-rosegold font-bold py-1">
                                    <Sparkles size={12} /> Join the waitlist
                                </Link>
                            </li>
                            <li>
                                <Link to="/d/clothing" className="block text-sm text-ink-soft hover:text-rosegold py-1">
                                    Meanwhile, shop Clothing
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Waitlist message */}
                    <div className="p-5 flex flex-col justify-center">
                        <p className="overline mb-2">Coming soon</p>
                        <h3 className="font-display text-3xl text-ink leading-tight mb-2">{dept.name}<br />is on the way</h3>
                        <p className="text-sm text-ink-soft leading-relaxed max-w-md">
                            We're hand-curating beautiful {dept.name.toLowerCase()} for you. Drop your email and we'll let you know the moment it goes live — one email, no spam.
                        </p>
                        <Link
                            to={`/d/${dept.slug}`}
                            data-testid={`mega-cs-cta-${dept.slug}`}
                            className="mt-4 inline-flex items-center gap-2 self-start px-4 h-10 rounded-full bg-ink text-ivory text-xs uppercase tracking-widest font-bold hover:bg-wine transition-colors"
                        >
                            Notify me <ArrowRight size={12} />
                        </Link>
                    </div>

                    {/* Editorial image right */}
                    <Link
                        to={`/d/${dept.slug}`}
                        className="relative bg-stone-100 overflow-hidden group block"
                        data-testid={`mega-editorial-${dept.slug}`}
                    >
                        {editorialImage ? (
                            <img src={editorialImage} alt={dept.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                        ) : (
                            <div className="absolute inset-0 bg-gradient-to-br from-rosegold/30 via-ivory-soft to-wine/20" />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/10 to-transparent" />
                        <div className="absolute inset-x-0 bottom-0 p-4 text-ivory">
                            <p className="font-heading text-xl leading-tight mb-1">{dept.name}</p>
                            <p className="text-[11px] uppercase tracking-widest font-bold inline-flex items-center gap-1 text-rosegold">
                                Notify me <ArrowRight size={11} />
                            </p>
                        </div>
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white border-2 border-rosegold/30 rounded-2xl shadow-[0_30px_90px_-12px_rgba(26,24,24,0.55),0_0_0_1px_rgba(255,255,255,0.6)_inset] overflow-hidden ring-1 ring-white/30">
            <div className="grid grid-cols-[200px_1fr_280px] min-h-[320px]">
                {/* Highlights column */}
                <div className="bg-ivory-soft border-r border-rosegold/10 p-5">
                    <p className="overline text-rosegold mb-3">{dept.name}</p>
                    <ul className="space-y-1.5">
                        <li>
                            <Link to={`/d/${dept.slug}`} data-testid={`mega-shop-all-${dept.slug}`} className="flex items-center gap-1.5 text-sm font-bold text-ink hover:text-rosegold py-1">
                                <ArrowRight size={13} /> Shop all {dept.name}
                            </Link>
                        </li>
                        <li>
                            <Link to={`/d/${dept.slug}?sort=newest`} className="flex items-center gap-1.5 text-sm text-ink-soft hover:text-rosegold py-1">
                                <Sparkles size={12} /> New arrivals
                            </Link>
                        </li>
                        <li>
                            <Link to="/sale" className="block text-sm text-wine font-semibold hover:underline py-1">
                                On sale
                            </Link>
                        </li>
                    </ul>
                </div>

                {/* Categories with icons */}
                <div className="p-5">
                    <p className="overline text-stone-500 mb-3">Categories</p>
                    {cats.length === 0 ? (
                        <p className="text-sm text-ink-soft italic">
                            {isLive ? "No categories yet." : "We're curating beautiful pieces — coming soon."}
                        </p>
                    ) : (
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                            {[col1, col2].map((col, ci) => (
                                <ul key={ci} className="space-y-1">
                                    {col.map(cat => (
                                        <li key={cat.id}>
                                            <Link
                                                to={isLive ? `/c/${encodeURIComponent(cat.name)}` : `/d/${dept.slug}`}
                                                data-testid={`mega-cat-${cat.id}`}
                                                className="flex items-center gap-2.5 group rounded-md py-1.5 px-1.5 -mx-1.5 hover:bg-ivory-soft transition-colors"
                                            >
                                                {cat.image_url ? (
                                                    <img src={cat.image_url} alt="" className="w-9 h-9 rounded-md border border-rosegold/15 object-cover flex-shrink-0" loading="lazy" />
                                                ) : (
                                                    <div className="w-9 h-9 rounded-md bg-ivory-soft border border-stone-200 flex-shrink-0" />
                                                )}
                                                <span className="text-sm text-ink group-hover:text-rosegold truncate">{cat.name}</span>
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            ))}
                        </div>
                    )}
                </div>

                {/* Editorial image right */}
                <Link
                    to={`/d/${dept.slug}`}
                    className="relative bg-stone-100 overflow-hidden group block"
                    data-testid={`mega-editorial-${dept.slug}`}
                >
                    {editorialImage ? (
                        <img src={editorialImage} alt={dept.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                    ) : (
                        <div className="absolute inset-0 bg-gradient-to-br from-rosegold/30 via-ivory-soft to-wine/20" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/10 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-4 text-ivory">
                        <p className="font-heading text-xl leading-tight mb-1">{dept.name}</p>
                        <p className="text-[11px] uppercase tracking-widest font-bold inline-flex items-center gap-1 text-rosegold">
                            {isLive ? "Shop the look" : "Notify me"} <ArrowRight size={11} />
                        </p>
                    </div>
                </Link>
            </div>
        </div>
    );
}
